import { useState, useEffect, useRef, useCallback } from 'react';
import { useError } from '../contexts/ErrorContext';

export const useDataFetching = (fetchFunction, dependencies = [], options = {}) => {
  const { showError } = useError();
  const { initialData = null, timeout = 15000 } = options;

  const [data, setData] = useState(initialData);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Используем useRef чтобы отслеживать, был ли уже сделан запрос
  const hasFetchedData = useRef(false);
  const requestController = useRef(null);

  // Выносим fetchData в useCallback с правильными зависимостями
  const fetchData = useCallback(async () => {
    // Если уже выполняется запрос, отменяем его
    if (requestController.current) {
      requestController.current.abort();
    }

    // Создаем новый контроллер для возможности отмены запроса
    requestController.current = new AbortController();

    setIsLoading(true);

    // Используем таймаут для запроса
    const timeoutId = setTimeout(() => {
      if (requestController.current) {
        requestController.current.abort();
      }
    }, timeout);

    try {
      const result = await fetchFunction();
      // Очищаем таймаут после успешного запроса
      clearTimeout(timeoutId);

      setData(result);
      hasFetchedData.current = true;
    } catch (error) {
      // Очищаем таймаут в случае ошибки
      clearTimeout(timeoutId);

      if (error.name === 'AbortError') {
        // Для прерванных запросов используем начальные данные
        setData(initialData);
      } else {
        showError(error.message || 'Error fetching data');
      }
    } finally {
      setIsLoading(false);
      requestController.current = null;
    }
  }, [fetchFunction, initialData, showError, timeout]); // Добавляем все зависимости

  const refreshData = useCallback(async () => {
    if (isRefreshing) return; // Предотвращаем параллельные обновления

    setIsRefreshing(true);

    // Создаем таймаут для обновления
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Refresh timed out')), timeout);
    });

    try {
      const result = await Promise.race([
        fetchFunction(),
        timeoutPromise
      ]);
      setData(result);
    } catch (error) {
      if (error.message === 'Refresh timed out') {
      } else {
        showError(error.message || 'Error refreshing data');
      }
    } finally {
      setIsRefreshing(false);
    }
  }, [fetchFunction, showError, timeout, isRefreshing]); // Добавляем isRefreshing как зависимость

  // Создаем мемоизированную строку зависимостей до useEffect
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const dependenciesString = JSON.stringify(dependencies);

  // Используем дополнительную проверку в useEffect
  useEffect(() => {
    // Предотвращаем повторную загрузку, если данные уже загружены и не требуется обновление
    if (!hasFetchedData.current) {
      fetchData();
    } else {
    }

    // Функция очистки при размонтировании компонента
    return () => {
      if (requestController.current) {
        requestController.current.abort();
      }
    };
  }, [dependenciesString, fetchData]); // Используем строковое представление зависимостей и fetchData

  // Позволяем принудительно обновить данные
  const forceFetch = useCallback(() => {
    hasFetchedData.current = false;
    fetchData();
  }, [fetchData]);

  return { data, isLoading, isRefreshing, refreshData, setData, forceFetch };
};